import * as React from 'react';
import styled from 'styled-components';
import {AppAssetCropModal} from './AppAssetCropModal';
import {ALVO} from './appAssetCrop';
import {
  AppAssetPreview,
  BotaoAsset,
  PreviewProps,
  TextoAuxiliar,
} from './AppAssetPresentation';
import {podeRecortar} from './imageCrop';

const Campo = styled.section`
  min-width: 0;
  border: 1px solid #dce5ee;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  color: #18334e;
  font-family: 'Open Sans', sans-serif;
  &,
  * {
    box-sizing: border-box;
  }
  h3 {
    color: #18334e;
    margin: 0;
    font-family: inherit;
    font-size: 20px;
    font-weight: 600;
  }
  && #preview-icon,
  && #preview-splashscreen {
    position: relative;
    top: auto;
    left: auto;
    width: 100%;
    height: auto;
    margin: 0;
    padding: 24px 12px;
    min-height: 300px;
    display: grid;
    align-items: center;
    background: #eff4f8;
  }
  && #preview-icon img {
    position: static;
    top: auto;
    left: auto;
  }
`;
interface Props extends PreviewProps {
  name: string;
  error?: string | null;
  corInicial?: string;
  cropIllustrationUrl?: string;
}
export const AppAssetField = (props: Props) => {
  const {kind, name, error, cropIllustrationUrl} = props;
  const seletor = React.useRef<HTMLInputElement>(null);
  const botaoEscolher = React.useRef<HTMLButtonElement>(null);
  const real = React.useRef<HTMLInputElement>(null);
  const urlConfirmada = React.useRef<string | null>(null);
  const [arquivo, setArquivo] = React.useState<File | null>(null);
  const [preview, setPreview] = React.useState(props.url || '');
  const [erro, setErro] = React.useState<string | null>(null);
  const [confirmado, setConfirmado] = React.useState(false);
  const [ilustracaoFalhou, setIlustracaoFalhou] = React.useState(false);
  React.useEffect(
    () => () => {
      if (urlConfirmada.current) URL.revokeObjectURL(urlConfirmada.current);
    },
    [],
  );
  React.useEffect(() => {
    const form = real.current?.form;
    const impedirEnvio = (evento: Event) => {
      if (arquivo || erro) {
        evento.preventDefault();
        if (!arquivo) botaoEscolher.current?.focus();
      }
    };
    form?.addEventListener('submit', impedirEnvio);
    return () => form?.removeEventListener('submit', impedirEnvio);
  }, [arquivo, erro]);
  const escolher = (evento: React.ChangeEvent<HTMLInputElement>) => {
    const escolhido = evento.target.files?.[0];
    evento.target.value = '';
    if (!escolhido) return;
    if (!podeRecortar(escolhido.type)) {
      setErro(
        'Escolha uma imagem PNG, JPG ou WebP. Converta arquivos HEIC, SVG ou GIF antes de enviar.',
      );
      return;
    }
    setErro(null);
    setArquivo(escolhido);
  };
  const confirmar = (recortado: File) => {
    const input = real.current;
    if (!input)
      throw new Error(
        'O campo de envio não está disponível. Recarregue a página.',
      );
    const anterior = input.files;
    let novaUrl: string | null = null;
    try {
      novaUrl = URL.createObjectURL(recortado);
      const transferencia = new DataTransfer();
      transferencia.items.add(recortado);
      input.files = transferencia.files;
      if (
        input.files?.length !== 1 ||
        input.files[0].name !== recortado.name ||
        input.files[0].size !== recortado.size ||
        input.files[0].type !== 'image/png'
      )
        throw new Error('Arquivo não atribuído.');
    } catch {
      if (novaUrl) URL.revokeObjectURL(novaUrl);
      try {
        input.files = anterior;
      } catch {
        input.value = '';
        setConfirmado(false);
        setPreview(props.url || '');
        setErro(
          'Selecione novamente a imagem: o navegador não conseguiu restaurar o arquivo preparado.',
        );
      }
      throw new Error(
        'Seu navegador não conseguiu preparar o envio. Tente uma versão atual do Chrome, Firefox ou Safari. Se o arquivo anterior não puder ser restaurado, será necessário selecionar a imagem novamente.',
      );
    }
    if (urlConfirmada.current) URL.revokeObjectURL(urlConfirmada.current);
    urlConfirmada.current = novaUrl;
    setPreview(novaUrl);
    setConfirmado(true);
    setErro(null);
    setArquivo(null);
  };
  const titulo = kind === 'icon' ? 'Ícone do aplicativo' : 'Tela de abertura';
  const id = kind === 'icon' ? 'preview-icon' : 'preview-splashscreen';
  return (
    <Campo aria-label={titulo}>
      <div style={{padding: '22px 24px'}}>
        <h3>{titulo}</h3>
        <TextoAuxiliar>
          {kind === 'icon'
            ? 'Sua marca na tela inicial do celular.'
            : 'A primeira imagem ao abrir seu aplicativo.'}
        </TextoAuxiliar>
      </div>
      <div id={id}>
        {preview ? (
          <AppAssetPreview {...props} url={preview} />
        ) : (
          <div style={{textAlign: 'center', maxWidth: 290, margin: 'auto'}}>
            {cropIllustrationUrl && !ilustracaoFalhou ? (
              <img
                src={cropIllustrationUrl}
                onError={() => setIlustracaoFalhou(true)}
                alt=""
                style={{
                  width: '100%',
                  maxWidth: 230,
                  height: 160,
                  objectFit: 'contain',
                  borderRadius: 12,
                }}
              />
            ) : (
              <div aria-hidden="true" style={{fontSize: 56, color: '#7896b1'}}>
                ▧
              </div>
            )}
            <TextoAuxiliar>
              Escolha sua imagem e ajuste o enquadramento com uma prévia em
              tempo real.
            </TextoAuxiliar>
          </div>
        )}
      </div>
      <div style={{padding: '20px 24px'}}>
        <input
          ref={seletor}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={escolher}
          aria-label={`Escolher ${titulo.toLowerCase()}`}
          style={{display: 'none'}}
        />
        <input
          ref={real}
          type="file"
          name={name}
          accept="image/png"
          aria-label={`Arquivo preparado: ${titulo.toLowerCase()}`}
          style={{display: 'none'}}
        />
        <BotaoAsset
          type="button"
          data-primary="true"
          ref={botaoEscolher}
          onClick={() => seletor.current?.click()}
        >
          {preview ? 'Trocar imagem' : 'Escolher imagem'}
        </BotaoAsset>
        <TextoAuxiliar>
          PNG, JPG ou WebP. Ajustamos para {ALVO[kind].largura} ×{' '}
          {ALVO[kind].altura} px.
        </TextoAuxiliar>
        {confirmado && (
          <p role="status" style={{fontSize: 13, color: '#236145'}}>
            Imagem preparada. Clique em Salvar para aplicar ao aplicativo.
          </p>
        )}
        {(erro || error) && (
          <div
            role="alert"
            style={{fontSize: 13, color: '#a12620', marginTop: 12}}
          >
            {erro || error}
            {error && (
              <p>
                Após uma falha ao salvar, selecione novamente as imagens que
                deseja enviar.
              </p>
            )}
            {erro && (
              <BotaoAsset type="button" onClick={() => setErro(null)}>
                Manter imagem anterior
              </BotaoAsset>
            )}
          </div>
        )}
      </div>
      {arquivo && (
        <AppAssetCropModal
          {...props}
          arquivo={arquivo}
          onCancel={() => setArquivo(null)}
          onConfirm={confirmar}
        />
      )}
    </Campo>
  );
};
